import { Building2, NotebookPen, Phone, Stethoscope, User } from "lucide-react";
import { APPOINTMENT_STATUS_COLORS, APPOINTMENT_STATUS_LABELS } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { getPatientColor, patientGradient } from "@/lib/patient-color";
import { SEXO_CURTO } from "@/lib/patient-sex";
import { formatPhone } from "@/components/patients/PatientListRow";
import type { WeekAppointment } from "@/lib/dashboard-week";
import { duracaoAgendada, idadeEmAnos, tempoDeAtendimento } from "./appt-card-info";

/**
 * A consulta do dia como cartão largo, em quatro faixas: QUANDO, QUEM, O QUÊ e SITUAÇÃO.
 *
 * Antes tudo empilhava num filete à esquerda e sobravam ~900px vazios à direita — a informação
 * ficava espremida num canto de um cartão largo. Agora cada faixa responde uma pergunta, e a
 * largura vira leitura: dá pra varrer a coluna de horários, a de pacientes ou a de procedimentos
 * de cima a baixo sem ler cartão por cartão.
 *
 * Consulta de outra organização (conta Pessoal) fica com a borda tracejada e o nome da clínica,
 * porque é só-leitura e a origem muda o que dá pra fazer com ela.
 */
export function ApptDayCard({
  appt,
  onSelect,
}: {
  appt: WeekAppointment;
  onSelect: (appt: WeekAppointment) => void;
}) {
  // Duas cores diferentes de propósito: a faixa é a cor do PROCEDIMENTO (a mesma leitura da grade
  // da Agenda), a pílula é a cor do STATUS. Usar a mesma variável nas duas fazia a pílula mentir —
  // "Agendado" saía pintado com a cor do procedimento.
  const corDoProcedimento = appt.color ?? APPOINTMENT_STATUS_COLORS[appt.status];
  const corDoStatus = APPOINTMENT_STATUS_COLORS[appt.status];
  const nome = appt.patient?.name ?? appt.patientName;
  const deFora = !!appt.ehDeOutraOrganizacao;

  const idade = idadeEmAnos(appt.patient?.birthDate);
  const telefone = appt.patient?.phone ?? appt.patientPhone;
  const tags = appt.patient?.tags ?? [];
  const real = tempoDeAtendimento(appt);

  const identificacao = [
    appt.patient?.identifier && `#${appt.patient.identifier}`,
    idade !== null && `${idade} anos`,
    appt.patient?.sex && (SEXO_CURTO[appt.patient.sex] ?? appt.patient.sex),
  ].filter(Boolean) as string[];

  return (
    <button
      type="button"
      onClick={() => onSelect(appt)}
      className={cn(
        // `min-h` dá corpo ao cartão mesmo na consulta sem observação nem tags — sem isso a lista
        // do dia alterna cartões de alturas diferentes e perde o ritmo.
        "flex min-h-[104px] w-full items-stretch gap-4 rounded-xl border bg-white p-4 text-left transition-all hover:border-zinc-300 hover:shadow-sm dark:bg-card",
        deFora ? "border-dashed border-zinc-300 dark:border-border" : "border-zinc-200 dark:border-border",
      )}
    >
      <span className="w-1 shrink-0 rounded-full" style={{ backgroundColor: corDoProcedimento }} aria-hidden />

      {/* QUANDO — largura fixa pra os horários formarem coluna entre um cartão e outro. As larguras
          das quatro faixas são fixas ou proporcionais justamente pra as divisórias alinharem de um
          cartão pro outro: é o que faz a lista ser lida em coluna, e não cartão a cartão. */}
      <div className="w-[116px] shrink-0 self-center">
        <p className="text-sm font-bold tabular-nums text-zinc-900 dark:text-foreground">
          {appt.startTime} – {appt.endTime}
        </p>
        <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">{duracaoAgendada(appt)}</p>
        {/* Tempo REAL só quando existe: é outra coisa que o horário agendado, e some quando a
            consulta ainda não começou. */}
        {real && (
          <p className="mt-0.5 text-[11px] font-semibold text-brand-700 dark:text-primary">{real}</p>
        )}
      </div>

      {/* QUEM */}
      {/* `0 1 340px`: não cresce (senão abre um vão entre o paciente e a divisória seguinte), mas
          encolhe quando o container aperta. */}
      <div className="flex min-w-0 flex-[0_1_340px] items-center gap-3 border-l border-zinc-100 pl-4 dark:border-border/60">
        {appt.patient?.avatarUrl ? (
          <img src={appt.patient.avatarUrl} alt="" className="h-12 w-12 shrink-0 rounded-full object-cover" />
        ) : (
          <span
            className="flex h-12 w-12 shrink-0 select-none items-center justify-center rounded-full text-base font-bold text-white"
            style={{ background: patientGradient(getPatientColor(nome)) }}
            aria-hidden
          >
            {nome.trim().charAt(0).toUpperCase()}
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate text-[15px] font-semibold leading-snug text-zinc-900 dark:text-foreground">
            {nome}
          </p>
          {identificacao.length > 0 && (
            <p className="truncate text-[11px] text-zinc-500 dark:text-muted-foreground">
              {identificacao.join(" · ")}
            </p>
          )}
          {telefone && (
            <p className="mt-0.5 flex items-center gap-1 text-[11px] tabular-nums text-zinc-500 dark:text-muted-foreground">
              <Phone className="h-3 w-3 shrink-0" />
              <span className="truncate">{formatPhone(telefone)}</span>
            </p>
          )}
        </div>
      </div>

      {/* O QUÊ */}
      <div className="hidden min-w-0 flex-1 flex-col justify-center gap-1 border-l border-zinc-100 pl-4 text-zinc-500 sm:flex dark:border-border/60 dark:text-muted-foreground">
        {appt.procedure && (
          <span className="flex min-w-0 items-center gap-1.5 text-sm">
            <Stethoscope className="h-4 w-4 shrink-0" />
            <span className="truncate font-medium text-zinc-700 dark:text-foreground/80">{appt.procedure}</span>
          </span>
        )}
        {appt.dentistName && (
          <span className="flex min-w-0 items-center gap-1.5 text-[13px]">
            <User className="h-4 w-4 shrink-0" />
            <span className="truncate">{appt.dentistName}</span>
          </span>
        )}
        {appt.notes && (
          <span className="flex min-w-0 items-center gap-1.5 text-xs" title={appt.notes}>
            <NotebookPen className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate italic">{appt.notes}</span>
          </span>
        )}
      </div>

      {/* SITUAÇÃO */}
      <div className="flex w-[168px] shrink-0 flex-col items-end justify-center gap-1.5">
        <span
          className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
          style={{ backgroundColor: `${corDoStatus}1f`, color: corDoStatus }}
        >
          {APPOINTMENT_STATUS_LABELS[appt.status]}
        </span>
        {/* Tags do paciente ficam aqui, e não junto do nome: são alerta (alergia, condição), então
            valem mais na borda que o olho varre do que perdidas no meio da identificação. */}
        {tags.length > 0 && (
          <span className="flex w-full flex-wrap justify-end gap-1">
            {tags.slice(0, 2).map((t) => (
              <span
                key={t}
                className="truncate rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600 dark:bg-muted dark:text-muted-foreground"
              >
                {t}
              </span>
            ))}
            {tags.length > 2 && (
              <span className="text-[10px] font-medium text-zinc-400 dark:text-muted-foreground">
                +{tags.length - 2}
              </span>
            )}
          </span>
        )}
        {deFora && appt.organizationName && (
          <span className="flex items-center gap-1 text-[11px] font-medium text-zinc-600 dark:text-foreground/70">
            <Building2 className="h-3 w-3 shrink-0" />
            <span className="max-w-[160px] truncate">{appt.organizationName}</span>
          </span>
        )}
      </div>
    </button>
  );
}
